import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Radio,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageCircle,
  FileText,
  CheckSquare,
  Square,
  Flame,
  Clock,
  Bot,
  PenTool,
  Layers,
  BarChart3,
  Settings,
  Zap,
  ArrowUpRight,
  CheckCircle,
  X,
  Plus,
  MapPin,
  Mail,
  Send,
  Pause,
  Play,
  Save,
  Loader2,
  Check,
  Globe,
} from "lucide-react";
import api from "../api/client";

const BUSINESSES = [
  { value: "", label: "All Businesses" },
  { value: "gcmm_home", label: "GCMM Home" },
  { value: "gcmm_dental", label: "GCMM Dental" },
  { value: "insidebalkan", label: "InsideBalkan" },
  { value: "usa_green_air", label: "USA Green Air" },
];

const STATUSES = [
  { value: "", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "reviewed", label: "Reviewed" },
  { value: "replied", label: "Replied" },
  { value: "blogged", label: "Blogged" },
  { value: "dismissed", label: "Dismissed" },
];

const SCORE_FILTERS = [
  { value: "", label: "All Scores" },
  { value: "60", label: "Hot (60+)" },
  { value: "40", label: "Warm (40+)" },
  { value: "20", label: "Low (20+)" },
];

const PLATFORMS = [
  { value: "", label: "All Platforms" },
  { value: "reddit", label: "Reddit" },
  { value: "quora", label: "Quora" },
];

const QUICK_FILTERS = [
  { label: "All", biz: "", score: "" },
  { label: "\ud83d\udd25 Hot", biz: "", score: "60" },
  { label: "\ud83c\udfe0 Home", biz: "gcmm_home", score: "" },
  { label: "\ud83e\uddb7 Dental", biz: "gcmm_dental", score: "" },
  { label: "\u2708\ufe0f Travel", biz: "insidebalkan", score: "" },
  { label: "\u2744\ufe0f HVAC", biz: "usa_green_air", score: "" },
];

function ScoreBadge({ score }) {
  let color = "bg-gray-600 text-gray-300";
  if (score >= 60) color = "bg-red-500/20 text-red-400 border border-red-500/30";
  else if (score >= 40) color = "bg-amber-500/20 text-amber-400 border border-amber-500/30";
  else if (score >= 20) color = "bg-blue-500/20 text-blue-400 border border-blue-500/30";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${color}`}>
      {score >= 60 && <Flame size={12} />}
      {score}
    </span>
  );
}

function AgeBadge({ post }) {
  if (!post.posted_at) {
    if (post.platform === "quora") {
      return <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-500/15 text-gray-400" title="Serper didn\u0027t return a date. Verify the question is still active.">Date unknown</span>;
    }
    return null;
  }
  const age = (Date.now() - new Date(post.posted_at).getTime()) / (1000 * 60 * 60);
  const days = Math.floor(age / 24);
  const hours = Math.floor(age);
  let label, color;
  if (hours < 24) {
    label = "Fresh";
    color = "bg-green-500/15 text-green-400";
  } else if (days <= 3) {
    label = `${days}d ago`;
    color = "bg-amber-500/15 text-amber-400";
  } else if (days <= 7) {
    label = `${days}d ago`;
    color = "bg-red-500/15 text-red-400";
  } else {
    label = "Stale";
    color = "bg-red-500/25 text-red-400";
  }
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${color}`}>{label}</span>;
}

function StatusBadge({ status }) {
  const colors = {
    new: "bg-copper/20 text-copper",
    reviewed: "bg-blue-500/20 text-blue-400",
    replied: "bg-green-500/20 text-green-400",
    blogged: "bg-purple-500/20 text-purple-400",
    dismissed: "bg-gray-600/40 text-gray-500",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors[status] || colors.new}`}>
      {status}
    </span>
  );
}


function TagInput({ value = [], onChange, placeholder = "Add area..." }) {
  const [input, setInput] = useState("");
  const addTag = () => {
    const tag = input.trim();
    if (tag && !value.includes(tag)) onChange([...value, tag]);
    setInput("");
  };
  const removeTag = (i) => onChange(value.filter((_, idx) => idx !== i));
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
    else if (e.key === "Backspace" && !input && value.length) removeTag(value.length - 1);
  };
  return (
    <div className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg focus-within:border-copper/50 transition-colors">
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-copper/15 text-copper text-xs rounded-md border border-copper/20">
            {tag}
            <button type="button" onClick={() => removeTag(i)} className="text-copper/60 hover:text-copper"><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} onBlur={addTag}
          placeholder={placeholder} className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 focus:outline-none" />
        {input.trim() && <button type="button" onClick={addTag} className="text-copper hover:text-copper-400"><Plus size={14} /></button>}
      </div>
    </div>
  );
}

function SchedulePanel() {
  const [schedules, setSchedules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => { api.get("/social/schedules/").then((res) => { setSchedules(res.data); setLoading(false); }); }, []);

  const allEnabled = schedules && schedules.scans.every((s) => s.enabled) && schedules.digests.every((d) => d.enabled);

  const handlePauseAll = async () => {
    const newEnabled = !allEnabled;
    setSaving("pause");
    await api.post("/social/schedules/pause-all/", { enabled: newEnabled });
    setSchedules((prev) => ({ scans: prev.scans.map((s) => ({ ...s, enabled: newEnabled })), digests: prev.digests.map((d) => ({ ...d, enabled: newEnabled })) }));
    setSaving(null);
  };

  const updateScan = async (id, data) => {
    setSaving(`scan-${id}`);
    const res = await api.patch(`/social/schedules/scan/${id}/`, data);
    setSchedules((prev) => ({ ...prev, scans: prev.scans.map((s) => (s.id === id ? res.data : s)) }));
    setSaving(null);
  };

  const updateDigest = async (id, data) => {
    setSaving(`digest-${id}`);
    const res = await api.patch(`/social/schedules/digest/${id}/`, data);
    setSchedules((prev) => ({ ...prev, digests: prev.digests.map((d) => (d.id === id ? res.data : d)) }));
    setSaving(null);
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={16} className="animate-spin text-copper" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock size={20} className="text-copper" />
          <h2 className="text-sm font-bold text-white">Scan & Digest Schedules</h2>
        </div>
        <button onClick={handlePauseAll} disabled={saving === "pause"}
          className={`px-4 py-2 text-xs font-semibold rounded-lg flex items-center gap-2 transition-colors ${allEnabled ? "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20" : "bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20"}`}>
          {saving === "pause" ? <Loader2 size={12} className="animate-spin" /> : allEnabled ? <Pause size={12} /> : <Play size={12} />}
          {allEnabled ? "Pause All" : "Resume All"}
        </button>
      </div>

      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase">Scans</h3>
        <div className="space-y-2">
          {schedules.scans.map((s) => (
            <div key={s.id} className="flex items-center gap-4 py-2 px-3 bg-dark/50 rounded-lg border border-dark-50/50">
              <button onClick={() => updateScan(s.id, { enabled: !s.enabled })} disabled={saving === `scan-${s.id}`}
                className={`w-8 h-5 rounded-full relative transition-colors ${s.enabled ? "bg-green-500" : "bg-gray-600"}`}>
                <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-transform ${s.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm font-medium w-20 ${s.enabled ? "text-white" : "text-gray-500"}`}>{s.priority_display}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Every</span>
                <select value={s.interval_minutes} onChange={(e) => updateScan(s.id, { interval_minutes: parseInt(e.target.value) })} disabled={!s.enabled}
                  className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                  {[5,10,15,30,60,120,360].map((v) => <option key={v} value={v}>{v < 60 ? `${v} min` : `${v/60} hr`}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Active</span>
                <select value={s.active_hours_start} onChange={(e) => updateScan(s.id, { active_hours_start: parseInt(e.target.value) })} disabled={!s.enabled}
                  className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                  {Array.from({length:24},(_,i)=>i).map((i) => <option key={i} value={i}>{i}:00</option>)}
                </select>
                <span className="text-xs text-gray-500">-</span>
                <select value={s.active_hours_end} onChange={(e) => updateScan(s.id, { active_hours_end: parseInt(e.target.value) })} disabled={!s.enabled}
                  className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                  {Array.from({length:24},(_,i)=>i).map((i) => <option key={i} value={i}>{i}:00</option>)}
                </select>
              </div>
              <span className="text-[10px] text-gray-600 ml-auto">{s.last_run ? `Last: ${new Date(s.last_run).toLocaleTimeString()}` : "Never run"}</span>
              {saving === `scan-${s.id}` && <Loader2 size={12} className="animate-spin text-copper" />}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase">Digests & Alerts</h3>
        <div className="space-y-2">
          {schedules.digests.map((d) => (
            <div key={d.id} className="flex items-center gap-4 py-2 px-3 bg-dark/50 rounded-lg border border-dark-50/50">
              <button onClick={() => updateDigest(d.id, { enabled: !d.enabled })} disabled={saving === `digest-${d.id}`}
                className={`w-8 h-5 rounded-full relative transition-colors ${d.enabled ? "bg-green-500" : "bg-gray-600"}`}>
                <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-transform ${d.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm font-medium w-28 ${d.enabled ? "text-white" : "text-gray-500"}`}>{d.digest_type_display}</span>
              {d.digest_type === "daily" ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">At</span>
                  <select value={d.hour} onChange={(e) => updateDigest(d.id, { hour: parseInt(e.target.value) })} disabled={!d.enabled}
                    className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                    {Array.from({length:24},(_,i)=>i).map((i) => <option key={i} value={i}>{i}:00 ET</option>)}
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">At :{String(d.minute).padStart(2,"0")} past hour,</span>
                  <select value={d.active_hours_start} onChange={(e) => updateDigest(d.id, { active_hours_start: parseInt(e.target.value) })} disabled={!d.enabled}
                    className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                    {Array.from({length:24},(_,i)=>i).map((i) => <option key={i} value={i}>{i}:00</option>)}
                  </select>
                  <span className="text-xs text-gray-500">-</span>
                  <select value={d.active_hours_end} onChange={(e) => updateDigest(d.id, { active_hours_end: parseInt(e.target.value) })} disabled={!d.enabled}
                    className="bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-copper/50 disabled:opacity-40">
                    {Array.from({length:24},(_,i)=>i).map((i) => <option key={i} value={i}>{i}:00</option>)}
                  </select>
                </div>
              )}
              <span className="text-[10px] text-gray-600 ml-auto">{d.last_run ? `Last: ${new Date(d.last_run).toLocaleTimeString()}` : "Never run"}</span>
              {saving === `digest-${d.id}` && <Loader2 size={12} className="animate-spin text-copper" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { api.get("/social/email-config/").then((res) => { setConfig(res.data); setDraft(res.data.recipient); setLoading(false); }); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { const res = await api.patch("/social/email-config/", { recipient: draft }); setConfig((p) => ({ ...p, recipient: res.data.recipient })); setEditing(false); }
    catch { alert("Error updating email"); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try { const res = await api.post("/social/email-test/", { recipient: config.recipient }); setTestResult({ success: true, message: res.data.message }); }
    catch (err) { setTestResult({ success: false, message: err.response?.data?.message || "Failed to send" }); }
    setTesting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={16} className="animate-spin text-copper" /></div>;

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Mail size={20} className="text-copper" />
        <h2 className="text-sm font-bold text-white">Email Alerts</h2>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <label className="text-xs text-gray-500 mb-1 block">Alert Recipient</label>
          {editing ? (
            <div className="flex items-center gap-2">
              <input type="email" value={draft} onChange={(e) => setDraft(e.target.value)}
                className="flex-1 px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
              <button onClick={handleSave} disabled={saving}
                className="px-3 py-2 bg-copper hover:bg-copper-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </button>
              <button onClick={() => { setEditing(false); setDraft(config.recipient); }} className="px-3 py-2 text-xs text-gray-500 hover:text-white">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white font-mono">{config.recipient}</span>
              <button onClick={() => setEditing(true)} className="px-2 py-1 text-[10px] text-gray-400 hover:text-copper border border-dark-50 rounded transition-colors">Edit</button>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">From</label>
          <span className="text-xs text-gray-400 font-mono">{config.from_email}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={handleTest} disabled={testing}
          className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-lg text-xs flex items-center gap-2 transition-colors disabled:opacity-50">
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send Test Email
        </button>
        {testResult && (
          <span className={`text-xs flex items-center gap-1 ${testResult.success ? "text-green-400" : "text-red-400"}`}>
            {testResult.success ? <Check size={12} /> : <X size={12} />} {testResult.message}
          </span>
        )}
      </div>
    </div>
  );
}

export default function SocialMonitor() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [stats, setStats] = useState({});
  const [keywords, setKeywords] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [scanLogs, setScanLogs] = useState([]);
  const [editProfile, setEditProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanningQuora, setScanningQuora] = useState(false);
  const [quota, setQuota] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);

  // Filters
  const [business, setBusiness] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");

  // Mode & selection
  const [mode, setMode] = useState(() => localStorage.getItem("sm_mode") || "ai");
  const [selected, setSelected] = useState(new Set());

  // Keyword/subreddit forms
  const [newKw, setNewKw] = useState({ keyword: "", category: "high_intent", business: "gcmm_home" });
  const [newSub, setNewSub] = useState({ name: "", priority: "medium", business: "gcmm_home" });

  const toggleMode = () => {
    const next = mode === "ai" ? "manual" : "ai";
    setMode(next);
    localStorage.setItem("sm_mode", next);
  };

  const fetchScanStatus = async () => {
    try {
      const res = await api.get("/social/scan/status/");
      setScanStatus(res.data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    fetchScanStatus();
    const interval = setInterval(fetchScanStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (business) params.set("business", business);
      if (statusFilter) params.set("status", statusFilter);
      if (scoreFilter) params.set("min_score", scoreFilter);
      if (searchQuery) params.set("search", searchQuery);
      if (platformFilter) params.set("platform", platformFilter);

      const [postsRes, statsRes, quotaRes] = await Promise.all([
        api.get(`/social/posts/?${params}`),
        api.get("/social/stats/"),
        api.get("/social/quota/"),
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
      setQuota(quotaRes.data);
    } catch (err) {
      console.error("Failed to fetch social data:", err);
    } finally {
      setLoading(false);
    }
  }, [business, statusFilter, scoreFilter, searchQuery, platformFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchBundles = async () => {
    try {
      const res = await api.get("/social/bundles/");
      setBundles(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchKeywords = async () => {
    try {
      const res = await api.get("/social/keywords/");
      setKeywords(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSubreddits = async () => {
    try {
      const res = await api.get("/social/subreddits/");
      setSubreddits(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await api.get("/social/profiles/");
      setProfiles(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchScanLogs = async () => {
    try {
      const res = await api.get("/social/scan-log/");
      setScanLogs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleProfileSave = async () => {
    if (!editProfile) return;
    setSavingProfile(editProfile.business);
    try {
      const res = await api.patch("/social/profiles/", editProfile);
      setProfiles((prev) => prev.map((p) => (p.business === editProfile.business ? res.data : p)));
      setEditProfile(null);
    } catch (err) {
      alert("Error saving profile");
    }
    setSavingProfile(null);
  };

  useEffect(() => {
    if (tab === "bundles") fetchBundles();
    if (tab === "keywords") fetchKeywords();
    if (tab === "subreddits") { fetchSubreddits(); fetchScanLogs(); }
    if (tab === "settings") fetchProfiles();
  }, [tab]);

  const handleScanNow = async () => {
    setScanning(true);
    setScanStatus({ state: "scanning", message: "Scanning subreddits..." });
    try {
      await api.post("/social/scan/now/");
      let polls = 0;
      const pollInterval = setInterval(async () => {
        polls++;
        try {
          const s = (await api.get("/social/scan/status/")).data;
          setScanStatus(s);
          if (s.state === "complete" || polls >= 24) {
            clearInterval(pollInterval);
            setScanning(false);
            fetchData();
          }
        } catch (e) {
          if (polls >= 24) {
            clearInterval(pollInterval);
            setScanning(false);
          }
        }
      }, 5000);
    } catch (err) {
      setScanning(false);
      setScanStatus({ state: "error", message: "Scan failed" });
    }
  };

  const handleScanQuora = async () => {
    setScanningQuora(true);
    try {
      await api.post("/social/scan/quora/");
      setTimeout(async () => {
        setScanningQuora(false);
        fetchData();
      }, 5000);
    } catch (err) {
      setScanningQuora(false);
      if (err.response?.status === 429) {
        alert("Serper.dev quota exhausted for this month");
      }
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAction = async (action) => {
    const ids = Array.from(selected);
    if (action === "dismiss") {
      await api.patch("/social/posts/", { ids, status: "dismissed" });
      fetchData();
      setSelected(new Set());
    } else if (action === "merged") {
      if (mode === "ai") {
        try {
          const res = await api.post("/social/bundles/merged/ai/", { post_ids: ids });
          navigate(`/social-monitor/bundle/${res.data.id}`);
        } catch (err) {
          alert(err.response?.data?.error || "Error creating merged blog");
        }
      } else {
        const title = prompt("Enter merged blog title:");
        if (title) {
          try {
            const res = await api.post("/social/bundles/merged/manual/", {
              post_ids: ids, title, content_markdown: "", meta_description: "",
            });
            navigate(`/social-monitor/bundle/${res.data.id}`);
          } catch (err) {
            alert(err.response?.data?.error || "Error");
          }
        }
      }
    } else if (action === "cluster") {
      if (mode === "ai") {
        try {
          const res = await api.post("/social/bundles/cluster/ai/", { post_ids: ids });
          navigate(`/social-monitor/bundle/${res.data.id}`);
        } catch (err) {
          alert(err.response?.data?.error || "Error creating cluster");
        }
      } else {
        const pillarTitle = prompt("Enter pillar article title:");
        if (pillarTitle) {
          const supCount = parseInt(prompt("How many supporting articles? (3-5)") || "3", 10);
          const titles = [];
          for (let i = 0; i < supCount; i++) {
            const t = prompt(`Supporting article ${i + 1} title:`);
            if (t) titles.push(t);
          }
          if (titles.length > 0) {
            try {
              const res = await api.post("/social/bundles/cluster/manual/skeleton/", {
                post_ids: ids, pillar_title: pillarTitle, supporting_titles: titles,
              });
              navigate(`/social-monitor/bundle/${res.data.id}`);
            } catch (err) {
              alert(err.response?.data?.error || "Error");
            }
          }
        }
      }
    }
  };

  const addKeyword = async () => {
    if (!newKw.keyword.trim()) return;
    await api.post("/social/keywords/", newKw);
    setNewKw({ keyword: "", category: "high_intent", business: newKw.business });
    fetchKeywords();
  };

  const deleteKeyword = async (id) => {
    await api.delete(`/social/keywords/${id}/`);
    fetchKeywords();
  };

  const addSubreddit = async () => {
    if (!newSub.name.trim()) return;
    await api.post("/social/subreddits/", newSub);
    setNewSub({ name: "", priority: "medium", business: newSub.business });
    fetchSubreddits();
  };

  const deleteSubreddit = async (id) => {
    await api.delete(`/social/subreddits/${id}/`);
    fetchSubreddits();
  };

  const tabs = [
    { key: "feed", label: "Feed", icon: Radio },
    { key: "bundles", label: "Bundles", icon: Layers },
    { key: "keywords", label: "Keywords", icon: Search },
    { key: "subreddits", label: "Subreddits", icon: MessageCircle },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Radio size={24} className="text-copper" />
          <h1 className="text-xl font-bold text-white">Social Monitor</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Mode toggle */}
          <button
            onClick={toggleMode}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              mode === "ai"
                ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                : "bg-blue-500/20 text-blue-400 border border-blue-500/30"
            }`}
          >
            {mode === "ai" ? <Bot size={14} /> : <PenTool size={14} />}
            {mode === "ai" ? "AI Draft" : "Manual"}
          </button>
          {/* Scan status + schedule info */}
          <div className="flex flex-col gap-0.5">
            {scanStatus && (
              <span className={`text-xs ${
                scanStatus.state === "complete" ? "text-green-400" :
                scanStatus.state === "scanning" ? "text-amber-400" :
                scanStatus.state === "error" ? "text-red-400" :
                "text-gray-500"
              }`}>
                {scanStatus.state === "complete" && <CheckCircle size={12} className="inline mr-1" />}
                {scanStatus.message}
              </span>
            )}
            {stats.last_scan && (
              <span className="text-[10px] text-gray-600">
                Last: {new Date(stats.last_scan).toLocaleDateString()} {new Date(stats.last_scan).toLocaleTimeString()}
                {stats.next_scan && <> · Next: {new Date(stats.next_scan).toLocaleDateString()} {new Date(stats.next_scan).toLocaleTimeString()}</>}
              </span>
            )}
          </div>
          <button
            onClick={handleScanQuora}
            disabled={scanningQuora}
            className="flex items-center gap-2 px-3 py-2 bg-teal-500/10 text-teal-400 border border-teal-500/20 text-xs font-semibold rounded-xl transition-colors disabled:opacity-50 hover:bg-teal-500/20"
          >
            <Globe size={14} className={scanningQuora ? "animate-spin" : ""} />
            {scanningQuora ? "Scanning..." : "Scan Quora"}
          </button>
          <button
            onClick={handleScanNow}
            disabled={scanning}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={scanning ? "animate-spin" : ""} />
            {scanning ? "Scanning..." : "Scan All"}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: "Total", value: stats.total_posts || 0, icon: BarChart3, onClick: () => { setStatusFilter(""); setScoreFilter(""); setPlatformFilter(""); } },
          { label: "Reddit", value: (stats.by_platform || []).find((p) => p.platform === "reddit")?.count || 0, icon: MessageCircle, onClick: () => { setPlatformFilter("reddit"); setStatusFilter(""); setScoreFilter(""); } },
          { label: "Quora", value: (stats.by_platform || []).find((p) => p.platform === "quora")?.count || 0, icon: Globe, onClick: () => { setPlatformFilter("quora"); setStatusFilter(""); setScoreFilter(""); } },
          { label: "Hot", value: stats.hot_posts || 0, icon: Flame, onClick: () => { setScoreFilter("60"); setStatusFilter(""); setPlatformFilter(""); } },
          { label: "Replied", value: stats.replied || 0, icon: MessageCircle, onClick: () => { setStatusFilter("replied"); setScoreFilter(""); setPlatformFilter(""); } },
          { label: "Blogged", value: stats.blogged || 0, icon: FileText, onClick: () => { setStatusFilter("blogged"); setScoreFilter(""); setPlatformFilter(""); } },
        ].map(({ label, value, icon: Icon, onClick }) => (
          <div key={label} onClick={onClick} className="bg-dark-300 border border-dark-50 rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:border-copper/40 transition-colors">
            <Icon size={16} className="text-gray-500" />
            <div>
              <div className="text-lg font-bold text-white">{value}</div>
              <div className="text-[10px] text-gray-500 uppercase">{label}</div>
            </div>
          </div>
        ))}
      </div>


      {/* Quota bar */}
      {quota && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-gray-500 uppercase font-semibold">Serper.dev API Quota</span>
            <span className="text-[10px] text-gray-400">{quota.monthly_used} / {quota.monthly_limit} queries ({quota.usage_pct}%)</span>
          </div>
          <div className="w-full h-1.5 bg-dark rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${quota.usage_pct >= 80 ? "bg-red-500" : quota.usage_pct >= 50 ? "bg-amber-500" : "bg-green-500"}`}
              style={{ width: `${Math.min(quota.usage_pct, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-600">Today: {quota.today_used}</span>
            <span className="text-[10px] text-gray-600">Remaining: {quota.remaining}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-dark-50">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              tab === key
                ? "text-copper border-copper"
                : "text-gray-400 border-transparent hover:text-white"
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "feed" && (
        <div className="space-y-4">
          {/* Quick filters */}
          <div className="flex flex-wrap gap-2">
            {QUICK_FILTERS.map(({ label, biz, score }) => (
              <button
                key={label}
                onClick={() => { setBusiness(biz); setScoreFilter(score); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  business === biz && scoreFilter === score
                    ? "bg-copper/20 text-copper border border-copper/30"
                    : "bg-dark-300 text-gray-400 border border-dark-50 hover:text-white hover:border-copper/20"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-dark-300 border border-dark-50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-copper/50 w-56"
              />
            </div>
            {[
              { val: business, set: setBusiness, opts: BUSINESSES },
              { val: statusFilter, set: setStatusFilter, opts: STATUSES },
              { val: scoreFilter, set: setScoreFilter, opts: SCORE_FILTERS },
              { val: platformFilter, set: setPlatformFilter, opts: PLATFORMS },
            ].map(({ val, set, opts }, i) => (
              <select
                key={i}
                value={val}
                onChange={(e) => set(e.target.value)}
                className="px-3 py-2 bg-dark-300 border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50"
              >
                {opts.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ))}
          </div>

          {/* Post list */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-500 text-sm">
                {stats.total_posts === 0
                  ? "No posts scanned yet. Click Scan Now to start, or wait for the next scheduled scan."
                  : "No posts match these filters. Try clearing filters or expanding the score range."}
              </div>
              {stats.total_posts > 0 && (
                <button
                  onClick={() => { setBusiness(""); setStatusFilter(""); setScoreFilter(""); setSearchQuery(""); }}
                  className="mt-3 px-4 py-2 text-xs text-copper border border-copper/30 rounded-lg hover:bg-copper/10"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-dark-300 border border-dark-50 rounded-xl p-4 hover:border-copper/20 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleSelect(post.id)}
                      className="mt-1 text-gray-500 hover:text-copper transition-colors"
                    >
                      {selected.has(post.id) ? <CheckSquare size={18} className="text-copper" /> : <Square size={18} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ScoreBadge score={post.score} />
                        <StatusBadge status={post.status} />
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${post.platform === "quora" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>{post.platform === "quora" ? "Quora" : `r/${post.subreddit}`}</span>
                        <span className="text-[10px] text-gray-600">{post.business_display}</span>
                        <AgeBadge post={post} />
                      </div>
                      <button
                        onClick={() => navigate(`/social-monitor/post/${post.id}`)}
                        className="text-sm text-white hover:text-copper font-medium text-left transition-colors"
                      >
                        {post.title}
                      </button>
                      {post.body && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{post.body.slice(0, 200)}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-600">
                        <span>{post.platform === "quora" ? "Quora" : `by u/${post.author}`}</span>
                        {post.platform !== "quora" && <span>{post.upvotes} upvotes</span>}
                        {post.platform !== "quora" && <span>{post.num_comments} comments</span>}
                        {post.keyword_matches?.length > 0 && (
                          <span className="text-copper">
                            {post.keyword_matches.filter((k) => k.category !== "negative").map((k) => k.keyword).join(", ")}
                          </span>
                        )}
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-copper"
                        >
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {post.reply_text && <MessageCircle size={14} className="text-green-500" />}
                      {post.blog_title && <FileText size={14} className="text-purple-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sticky action bar */}
          {selected.size >= 2 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-dark-300 border border-copper/30 rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4 z-50">
              <span className="text-sm text-gray-400">{selected.size} selected</span>
              {selected.size >= 2 && selected.size <= 5 && (
                <button
                  onClick={() => handleBulkAction("merged")}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-semibold hover:bg-purple-500/30 transition-colors"
                >
                  {mode === "ai" ? "AI Merged Blog" : "Manual Merged Blog"}
                </button>
              )}
              {selected.size >= 6 && (
                <button
                  onClick={() => handleBulkAction("cluster")}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm font-semibold hover:bg-blue-500/30 transition-colors"
                >
                  {mode === "ai" ? "AI Content Cluster" : "Manual Cluster"}
                </button>
              )}
              <button
                onClick={() => handleBulkAction("dismiss")}
                className="px-4 py-2 bg-gray-700/50 text-gray-400 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-gray-500 hover:text-white text-sm"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "bundles" && (
        <div className="space-y-3">
          {bundles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No bundles yet. Select 2+ posts to create a merged blog or content cluster.</div>
          ) : (
            bundles.map((b) => (
              <button
                key={b.id}
                onClick={() => navigate(`/social-monitor/bundle/${b.id}`)}
                className="w-full bg-dark-300 border border-dark-50 rounded-xl p-4 text-left hover:border-copper/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        b.bundle_type === "merged"
                          ? "bg-purple-500/20 text-purple-400"
                          : "bg-blue-500/20 text-blue-400"
                      }`}>
                        {b.bundle_type_display}
                      </span>
                      <span className="text-[10px] text-gray-600">{b.source_mode_display}</span>
                      <span className="text-[10px] text-gray-600">{b.business_display}</span>
                    </div>
                    <div className="text-sm text-white font-medium">
                      {b.merged_title || b.pillar_title || `Bundle #${b.id}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-500">
                    <span className="text-xs">{b.post_count} posts</span>
                    <ArrowUpRight size={14} />
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {tab === "keywords" && (
        <div className="space-y-4">
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Add Keyword</h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Keyword..."
                value={newKw.keyword}
                onChange={(e) => setNewKw({ ...newKw, keyword: e.target.value })}
                className="flex-1 px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-copper/50"
              />
              <select
                value={newKw.category}
                onChange={(e) => setNewKw({ ...newKw, category: e.target.value })}
                className="px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white"
              >
                <option value="high_intent">High Intent</option>
                <option value="location_boost">Location Boost</option>
                <option value="negative">Negative</option>
              </select>
              <select
                value={newKw.business}
                onChange={(e) => setNewKw({ ...newKw, business: e.target.value })}
                className="px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white"
              >
                {BUSINESSES.filter((b) => b.value).map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              <button onClick={addKeyword} className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-lg">
                Add
              </button>
            </div>
          </div>
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-50 text-gray-500 text-left text-xs uppercase">
                  <th className="px-4 py-3">Keyword</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => (
                  <tr key={kw.id} className="border-b border-dark-50/50 hover:bg-dark-50/20">
                    <td className="px-4 py-2.5 text-white">{kw.keyword}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        kw.category === "high_intent" ? "bg-green-500/20 text-green-400"
                        : kw.category === "location_boost" ? "bg-blue-500/20 text-blue-400"
                        : "bg-red-500/20 text-red-400"
                      }`}>
                        {kw.category.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">{BUSINESSES.find((b) => b.value === kw.business)?.label}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => deleteKeyword(kw.id)} className="text-gray-600 hover:text-red-400 text-xs">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "subreddits" && (
        <div className="space-y-4">
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Add Subreddit</h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Subreddit name (without r/)..."
                value={newSub.name}
                onChange={(e) => setNewSub({ ...newSub, name: e.target.value })}
                className="flex-1 px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-copper/50"
              />
              <select
                value={newSub.priority}
                onChange={(e) => setNewSub({ ...newSub, priority: e.target.value })}
                className="px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={newSub.business}
                onChange={(e) => setNewSub({ ...newSub, business: e.target.value })}
                className="px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white"
              >
                {BUSINESSES.filter((b) => b.value).map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              <button onClick={addSubreddit} className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-lg">
                Add
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-dark-50">
                <h3 className="text-sm font-semibold text-white">Monitored Subreddits</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-50 text-gray-500 text-left text-xs uppercase">
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Priority</th>
                    <th className="px-4 py-2">Business</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {subreddits.map((sub) => (
                    <tr key={sub.id} className="border-b border-dark-50/50 hover:bg-dark-50/20">
                      <td className="px-4 py-2 text-white font-mono text-xs">r/{sub.name}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          sub.priority === "high" ? "bg-red-500/20 text-red-400"
                          : sub.priority === "medium" ? "bg-amber-500/20 text-amber-400"
                          : "bg-gray-600/40 text-gray-400"
                        }`}>
                          {sub.priority}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs">{BUSINESSES.find((b) => b.value === sub.business)?.label}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => deleteSubreddit(sub.id)} className="text-gray-600 hover:text-red-400 text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-dark-50">
                <h3 className="text-sm font-semibold text-white">Recent Scan Logs</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {scanLogs.slice(0, 30).map((log) => (
                  <div key={log.id} className="px-4 py-2 border-b border-dark-50/50 text-xs flex items-center justify-between">
                    <span className="text-white font-mono">{log.subreddit.startsWith("quora:") ? `Q: ${log.subreddit.replace("quora:", "")}` : `r/${log.subreddit}`}</span>
                    <div className="flex items-center gap-3 text-gray-500">
                      <span>{log.posts_matched}/{log.posts_found} matched</span>
                      <span>{log.scan_time_ms}ms</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-6">
          {/* Celery Control Panel */}
          <SchedulePanel />

          {/* Email Alerts */}
          <EmailPanel />

          {/* Business Profiles */}
          <div className="flex items-center gap-3">
            <Settings size={18} className="text-copper" />
            <h2 className="text-sm font-bold text-white">Business Profiles</h2>
          </div>

          {profiles.map((profile) => {
            const isEditing = editProfile?.business === profile.business;
            const p = isEditing ? editProfile : profile;
            return (
              <div key={profile.business} className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: p.primary_color }} />
                    <h3 className="text-sm font-semibold text-white">{p.company_name}</h3>
                    <span className="text-[10px] text-gray-500 uppercase">{p.business_display}</span>
                  </div>
                  {!isEditing ? (
                    <button onClick={() => setEditProfile({ ...profile })}
                      className="px-3 py-1.5 text-xs text-gray-400 hover:text-copper border border-dark-50 rounded-lg transition-colors">Edit</button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => setEditProfile(null)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-white">Cancel</button>
                      <button onClick={handleProfileSave} disabled={savingProfile === p.business}
                        className="px-4 py-1.5 bg-copper hover:bg-copper-600 text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50">
                        {savingProfile === p.business ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                      </button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Company Name</label>
                      <input type="text" value={p.company_name} onChange={(e) => setEditProfile({ ...p, company_name: e.target.value })}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Website URL</label>
                      <input type="text" value={p.website_url} onChange={(e) => setEditProfile({ ...p, website_url: e.target.value })}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 mb-1 block">Description</label>
                      <textarea value={p.description} onChange={(e) => setEditProfile({ ...p, description: e.target.value })} rows={2}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <MapPin size={12} /> Service Areas
                        <span className="text-gray-600 font-normal">(auto-synced to location keywords)</span>
                      </label>
                      <TagInput value={p.service_areas || []} onChange={(areas) => setEditProfile({ ...p, service_areas: areas })}
                        placeholder="Type area and press Enter..." />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Primary Color</label>
                      <input type="color" value={p.primary_color} onChange={(e) => setEditProfile({ ...p, primary_color: e.target.value })}
                        className="w-12 h-8 rounded cursor-pointer" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">CTA Text</label>
                      <input type="text" value={p.cta_text} onChange={(e) => setEditProfile({ ...p, cta_text: e.target.value })}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 mb-1 block">Reply Context</label>
                      <textarea value={p.reply_context} onChange={(e) => setEditProfile({ ...p, reply_context: e.target.value })} rows={2}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 mb-1 block">Blog Context</label>
                      <textarea value={p.blog_context} onChange={(e) => setEditProfile({ ...p, blog_context: e.target.value })} rows={2}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 mb-1 block">Bio Text</label>
                      <textarea value={p.bio_text} onChange={(e) => setEditProfile({ ...p, bio_text: e.target.value })} rows={2}
                        className="w-full px-3 py-2 bg-dark border border-dark-50 rounded-lg text-sm text-white focus:outline-none focus:border-copper/50" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                    <div>
                      <span className="text-gray-600">Website:</span>{" "}
                      <a href={p.website_url} target="_blank" rel="noopener" className="text-copper hover:underline">{p.website_url}</a>
                    </div>
                    <div><span className="text-gray-600">CTA:</span> {p.cta_text}</div>
                    <div className="col-span-2"><span className="text-gray-600">Services:</span> {(p.services || []).join(", ")}</div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Service Areas:</span>{" "}
                      {(p.service_areas || []).length > 0
                        ? <span className="text-gray-400">{(p.service_areas || []).join(", ")}</span>
                        : <span className="text-gray-600 italic">None set</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
